import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from 'react-redux';
import { notification, Button, Space } from "antd";
import { FlagOutlined } from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import dayjs from "dayjs";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import Excelexport from "../../utils/excelexport.jsx";
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDate, compareDates } from '../../utils/dates';
import { useGetModelsLookupQuery, useGetVersionsLookupQuery,
         useGetTenantsLookupQuery, invalidateRoutersLookup } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function RouterAssignedList({ lang }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ loading: false });

  const [data, setData] = useState({
    raw: [],
    selected: [],
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const dispatch = useDispatch();
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();

  // ===== 3. DERIVADOS =====
  const modelFilter = useMemo(
    () => models
      .filter(model => model.device_type === 'router')
      .map(model => ({ text: model.name, value: model.uuid })),
    [models]
  );

  const versionFilter = useMemo(
    () => versions
      .filter(version => version.device_type === 'router')
      .map(version => ({ text: version.name, value: version.uuid })),
    [versions]
  );

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    try {
      const res = await service.get("routers/manufacture/Asignado");

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data.data || []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [lang]);

  /**
   * Calcula los datos enriquecidos para la lista de routers asignados
   */
  const enrichedData = useMemo(() => {
    return data.raw.map((element) => ({
      ...element,
      dateFormated: formatDate(element.date) || '-',
      tenant_name: tenants.find(tenant => tenant.uuid === element.tenant_uuid)?.name ?? '-',
      vpnLabel: element.vpn ? lang.handle('Sí') : lang.handle('No'),
    }));
  }, [data.raw, tenants, lang]);

  // ===== 6. HANDLERS =====
  /**
   * Marca los routers seleccionados como finalizados
   */
  const finishRouters = async (e) => {
    e.preventDefault();
    updateUI('loading', true);

    for (const uuid of data.selected.map(row => row.uuid)) {
      try {
        const res = await service.post("routers/finish/" + uuid);

        if (!res || res.data.ok === false) {
          notification["error"]({ message: lang.handle('No se ha podido terminar') });
        } else {
          notification["success"]({ message: lang.handle('Router terminado') });
        }
      } catch (err) {
        console.error("Error devuelto: " + err);
        notification["error"]({ message: lang.handle('No se ha podido terminar') });
      }
    }

    updateData('selected', []);
    dispatch(invalidateRoutersLookup());
    await Pfetch();
    updateUI('loading', false);
  };
  
  /**
   * Prepara los datos seleccionados para exportación
   */
  const getExportData = () => {
    return data.selected.map(row => ({
      Pedido: row.order_id ?? '',
      IMEI: row.modem_imei,
      Tipo_teltonika: row.modem_type_code,
      Numero_serie: row.modem_serial_number ?? '',
      MAC: row.modem_mac ?? '',
      Modelo: row.model_name,
      Version: row.version_name,
      Organizacion: row.tenant_name,
      ICC: row.icc ?? '',
      VPN: row.vpnLabel,
    }));
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch().then(() => updateUI('loading', false));
  }, [Pfetch]);

  // ===== 8. CONFIG =====
  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys: data.selected.map(row => row.uuid),
    onChange: (selectedRowKeys, selectedRows) => {
      updateData('selected', selectedRows);
    },
  };

  const columns = [
    {
      title: lang.handle('Pedido'),
      dataIndex: "order_id",
      ...GetColumnLiteralSearchProps("order_id", lang),
    },
    {
      title: "UUID",
      dataIndex: "uuid",
      ...GetColumnLiteralSearchProps("uuid", lang),
    },
    {
      title: "IMEI",
      dataIndex: "modem_imei",
      ...GetColumnLiteralSearchProps("modem_imei", lang),
    },
    {
      title: lang.handle('Tipo Teltonika'),
      dataIndex: "modem_type_code",
      ...GetColumnLiteralSearchProps("modem_type_code", lang),
    },
    {
      title: lang.handle('Modelo'),
      dataIndex: "model_name",
      filters: modelFilter,
      onFilter: (value, record) => record.model_uuid === value,
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
    },
    {
      title: lang.handle('Organización'),
      dataIndex: "tenant_name",
      ...GetColumnLiteralSearchProps("tenant_name", lang),
    },
    {
      title: "ICC",
      dataIndex: "icc",
      ...GetColumnLiteralSearchProps("icc", lang),
    },
    {
      title: "VPN",
      dataIndex: "vpnLabel",
      filters: [
        { text: lang.handle('Sí'), value: 1 },
        { text: lang.handle('No'), value: 0 },
      ],
      onFilter: (value, record) => (record.vpn ? 1 : 0) === value,
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "dateFormated",
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
    },
  ];

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={hasPrivilege('assign_finish') ? rowSelection : null}
        columns={columns}
        rowKey="uuid"
        dataSource={enrichedData}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('assign_finish') && (
            <Button
              type="primary"
              icon={<FlagOutlined />}
              disabled={data.selected.length === 0}
              onClick={finishRouters}
            >
              {lang.handle('Terminar')}
            </Button>
          )}
        </Space>
        <Space>
          <Excelexport
            fileName={'routers_asignados_' + dayjs().format('YYYYMMDD_HHmmss')}
            fileData={() => [{ name: 'routers_asignados', data: getExportData() }]}
            disabled={data.selected.length === 0}
            label={lang.handle('Exportar a Excel')}
          />
        </Space>
      </div>
    </div>
  );
}

export default withLocale(RouterAssignedList);
